// Use the antd direct path import here. The shared `import { message } from
// 'antd'` form is rewritten by babel-plugin-import (configured in
// config-overrides.js), and that rewrite was eliding the `message` binding
// in this file at runtime ("ReferenceError: message is not defined").
// Direct path matches what the plugin would output anyway.
import message from 'antd/es/message';

/**
 * AER-1587 — Toast UX improvements:
 *  1) Dedupe toasts by level so a burst of errors collapses to one on-screen
 *     toast (common with bulk request failures on mobile). Different levels
 *     remain independent — an error and an info can both display together.
 *  2) Dismiss all toasts when the user taps anywhere on a visible toast
 *     so they don't linger and block the UI on small screens.
 *
 * Antd 3.x's `message` API doesn't natively dedupe, so we monkey-patch each
 * level method with a small dedupe wrapper.  Using static member access
 * (message.error, message.warning, …) keeps babel-plugin-import happy —
 * dynamic message[level] access would not generate the right bindings.
 */

type Level = 'error' | 'warning' | 'warn' | 'info' | 'success' | 'loading';

const DEDUPE_WINDOW_MS = 3000;

const activeMessages = new Map<Level, number>();

// tslint:disable-next-line:no-any
type AnyFn = (...args: any[]) => any;

const makeWrapped = (level: Level, original: AnyFn): AnyFn => {
  // tslint:disable-next-line:no-any
  const wrapped = function(content: any, duration?: any, onClose?: any): any {
    const now = Date.now();
    const lastShown = activeMessages.get(level);
    if (lastShown && now - lastShown < DEDUPE_WINDOW_MS) {
      const noop = () => {
        return;
      };
      // tslint:disable-next-line:no-any
      const stub: any = noop;
      stub.then = (resolve: (v: void) => void) => {
        if (typeof resolve === 'function') {
          resolve(undefined as void);
        }
        return Promise.resolve();
      };
      stub.promise = Promise.resolve();
      return stub;
    }

    activeMessages.set(level, now);

    const wrappedOnClose = () => {
      activeMessages.delete(level);
      if (typeof onClose === 'function') {
        try {
          onClose();
        } catch {
          // ignore
        }
      }
    };

    if (typeof duration === 'function') {
      const cb = duration;
      const wrappedCb = () => {
        activeMessages.delete(level);
        try {
          cb();
        } catch {
          // ignore
        }
      };
      return original(content, wrappedCb);
    }

    return original(content, duration, wrappedOnClose);
  };

  (wrapped as { __aerWrapped?: boolean }).__aerWrapped = true;
  return wrapped;
};

const isWrapped = (fn: AnyFn | undefined): boolean => {
  return !!fn && (fn as { __aerWrapped?: boolean }).__aerWrapped === true;
};

let initialized = false;

export const initToastHelper = (): void => {
  if (initialized) {
    return;
  }
  initialized = true;

  // Static member access — each level patched explicitly.
  if (message.error && !isWrapped(message.error)) {
    // tslint:disable-next-line:no-any
    (message as any).error = makeWrapped('error', message.error);
  }
  if (message.warning && !isWrapped(message.warning)) {
    // tslint:disable-next-line:no-any
    (message as any).warning = makeWrapped('warning', message.warning);
  }
  // tslint:disable-next-line:no-any
  const warnRef = (message as any).warn as AnyFn | undefined;
  if (warnRef && !isWrapped(warnRef)) {
    // tslint:disable-next-line:no-any
    (message as any).warn = makeWrapped('warn', warnRef);
  }
  if (message.info && !isWrapped(message.info)) {
    // tslint:disable-next-line:no-any
    (message as any).info = makeWrapped('info', message.info);
  }
  if (message.success && !isWrapped(message.success)) {
    // tslint:disable-next-line:no-any
    (message as any).success = makeWrapped('success', message.success);
  }
  if (message.loading && !isWrapped(message.loading)) {
    // tslint:disable-next-line:no-any
    (message as any).loading = makeWrapped('loading', message.loading);
  }

  if (typeof document !== 'undefined') {
    const dismissOnTap = (e: Event) => {
      const target = e.target as Element | null;
      if (!target || typeof target.closest !== 'function') {
        return;
      }
      if (target.closest('.ant-message-notice')) {
        message.destroy();
        activeMessages.clear();
      }
    };
    document.addEventListener('click', dismissOnTap, true);
    document.addEventListener('touchstart', dismissOnTap, true);
  }
};
